/**
 * Manager PA - Team Workload Board
 * Per-staff open/overdue task counts with a capacity/load indicator,
 * plus an inline "Assign task" form that posts to /todo/create.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, UserPlus, CheckSquare, Clock, AlertTriangle, Loader2, Plus, X, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';
import { SkeletonPulse2 } from '@/components/pwa/Skeleton';

type TeamTask = {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assigned_to: string | null;
  assigned_to_email?: string | null;
  assigned_to_name?: string | null;
  created_at: string;
};

type TeamMember = {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
};

type Workload = {
  member: TeamMember;
  open: number;
  overdue: number;
  inProgress: number;
  completed: number;
};

export default function TeamPage() {
  const queryClient = useQueryClient();
  const [showAssign, setShowAssign] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', priority: 'medium', dueDate: '', assignedTo: '' });

  const { data: stats } = useQuery({
    queryKey: ['todo-stats'],
    queryFn: async () => { const r = await apiClient.get('/todo/stats'); return r.data.data; },
  });
  const { data: teamTasks, isLoading: loadingTeam } = useQuery<TeamTask[]>({
    queryKey: ['todo-team'],
    queryFn: async () => { const r = await apiClient.get('/todo/team'); return r.data.data || []; },
  });
  const { data: teamMembers } = useQuery<TeamMember[]>({
    queryKey: ['todo-users'],
    queryFn: async () => { const r = await apiClient.get('/todo/users'); return r.data.data || []; },
  });

  const invalidateAll = () => { ['todo-stats', 'todo-team'].forEach(k => queryClient.invalidateQueries({ queryKey: [k] })); };
  const createMutation = useMutation({
    mutationFn: async (task: any) => { const r = await apiClient.post('/todo/create', task); return r.data.data; },
    onSuccess: () => { invalidateAll(); setShowAssign(false); setNewTask({ title: '', priority: 'medium', dueDate: '', assignedTo: '' }); },
  });

  const isOverdue = (d: string | null) => !!d && new Date(d) < new Date();
  const memberName = (m: TeamMember) => [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email?.split('@')[0] || 'Member';

  // Build per-staff workload from team task list + users list
  const workloads = useMemo<Workload[]>(() => {
    const members = teamMembers || [];
    const tasks = teamTasks || [];
    return members.map(member => {
      const mine = tasks.filter(t => t.assigned_to === member.id);
      const open = mine.filter(t => t.status === 'pending' || t.status === 'in_progress').length;
      const overdue = mine.filter(t => t.status !== 'completed' && isOverdue(t.due_date)).length;
      const inProgress = mine.filter(t => t.status === 'in_progress').length;
      const completed = mine.filter(t => t.status === 'completed').length;
      return { member, open, overdue, inProgress, completed };
    }).sort((a, b) => b.open - a.open);
  }, [teamMembers, teamTasks]);

  const maxOpen = Math.max(1, ...workloads.map(w => w.open));
  const totalOpen = workloads.reduce((s, w) => s + w.open, 0);

  // Capacity/load tiers (legible in light + dark)
  const loadTier = (open: number) => {
    if (open >= 6) return { label: 'High load', cls: 'text-red-600 dark:text-red-400 bg-red-500/10', bar: 'bg-red-500' };
    if (open >= 3) return { label: 'Medium', cls: 'text-amber-700 dark:text-amber-300 bg-amber-500/10', bar: 'bg-amber-500' };
    return { label: 'Light', cls: 'text-green-600 dark:text-green-400 bg-green-500/10', bar: 'bg-[var(--v4-accent)]' };
  };

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-4 animate-[slideUp_0.2s_ease-out] overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium flex items-center gap-2 text-foreground">
          <Users className="h-5 w-5 text-[var(--v4-accent)]" /> Team Workload
        </h2>
        <button onClick={() => setShowAssign(true)} className="px-3 py-1.5 bg-[var(--v4-accent)] text-white rounded-lg text-xs font-medium hover:bg-[var(--v4-accent-hover)] transition flex items-center gap-1">
          <UserPlus className="h-3.5 w-3.5" /> Assign task
        </button>
      </div>

      {/* Aggregate stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-card border border-border rounded-xl p-2.5 text-center">
          <p className="text-lg font-bold text-[var(--v4-accent)]">{stats?.team_pending ?? totalOpen}</p>
          <p className="text-[10px] text-muted-foreground">Open</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-2.5 text-center">
          <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{(teamMembers || []).length}</p>
          <p className="text-[10px] text-muted-foreground">Staff</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-2.5 text-center">
          <p className="text-lg font-bold text-green-600 dark:text-green-400">{stats?.my_completed ?? 0}</p>
          <p className="text-[10px] text-muted-foreground">Done (you)</p>
        </div>
      </div>

      {/* Assign Task form */}
      {showAssign && (
        <div className="bg-card border border-border rounded-xl p-4 space-y-3 animate-[slideUp_0.2s_ease-out]">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground flex items-center gap-1.5"><UserPlus className="h-4 w-4 text-[var(--v4-accent)]" /> Assign a task</span>
            <button onClick={() => setShowAssign(false)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          </div>
          <input
            placeholder="Task title"
            value={newTask.title}
            onChange={e => setNewTask({ ...newTask, title: e.target.value })}
            className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none"
          />
          <select
            value={newTask.assignedTo}
            onChange={e => setNewTask({ ...newTask, assignedTo: e.target.value })}
            className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm outline-none text-foreground [&>option]:bg-card [&>option]:text-foreground"
          >
            <option value="">Assign to me</option>
            {(teamMembers || []).map(m => <option key={m.id} value={m.id}>{memberName(m)}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={newTask.priority}
              onChange={e => setNewTask({ ...newTask, priority: e.target.value })}
              className="bg-muted border border-border rounded-lg px-3 py-2 text-sm outline-none text-foreground [&>option]:bg-card [&>option]:text-foreground"
            >
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
            <input
              type="date"
              value={newTask.dueDate}
              onChange={e => setNewTask({ ...newTask, dueDate: e.target.value })}
              className="bg-muted border border-border rounded-lg px-3 py-2 text-sm outline-none text-foreground [&>option]:bg-card [&>option]:text-foreground"
            />
          </div>
          <button
            onClick={() => { if (newTask.title) createMutation.mutate(newTask); }}
            disabled={createMutation.isPending || !newTask.title}
            className="w-full py-2 bg-[var(--v4-accent)] text-white rounded-lg text-sm font-medium hover:bg-[var(--v4-accent-hover)] transition disabled:opacity-40 flex items-center justify-center gap-1"
          >
            {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create &amp; assign
          </button>
        </div>
      )}

      {/* Workload board */}
      {loadingTeam ? <SkeletonPulse2 /> : (
        <div className="space-y-2">
          {workloads.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No team members</p>
            </div>
          ) : workloads.map(w => {
            const tier = loadTier(w.open);
            const pct = Math.round((w.open / maxOpen) * 100);
            return (
              <div key={w.member.id} className="bg-card border border-border rounded-xl p-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[var(--v4-accent)]/15 flex items-center justify-center shrink-0">
                    <User className="h-4 w-4 text-[var(--v4-accent)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{memberName(w.member)}</p>
                    {w.member.role && <p className="text-[10px] text-muted-foreground capitalize truncate">{w.member.role}</p>}
                  </div>
                  <span className={cn('text-[10px] px-2 py-0.5 rounded-lg font-medium shrink-0', tier.cls)}>{tier.label}</span>
                </div>

                {/* Load bar */}
                <div className="mt-3">
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div className={cn('h-full rounded-full transition-all', tier.bar)} style={{ width: `${pct}%` }} />
                  </div>
                </div>

                {/* Counts */}
                <div className="flex items-center gap-3 mt-2.5 text-[11px]">
                  <span className="flex items-center gap-1 text-foreground">
                    <CheckSquare className="h-3 w-3 text-[var(--v4-accent)]" />{w.open} open
                  </span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-3 w-3" />{w.inProgress} active
                  </span>
                  {w.overdue > 0 && (
                    <span className="flex items-center gap-1 text-red-600 dark:text-red-400 font-medium">
                      <AlertTriangle className="h-3 w-3" />{w.overdue} overdue
                    </span>
                  )}
                  <span className="ml-auto text-muted-foreground">{w.completed} done</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
